import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { gqlBillingCadenceToBillingCadence } from '../../../../lib/subscription';
import { Radio, Stack, Text } from '@mantine/core';
import { getBillingCadenceDescription, getShortSubscriptionName } from '../../util/helpers';
import { SubscriptionOption } from '../../../../components/SubscriptionOptionDetails';
import { subscriptionOption as GQLSubscriptionOption } from '../../../../types/gql-op-types';
import { getLocalizedPricing } from '../../../../lib/util';
import { useLocale } from '../../../../contexts/LocaleContext';

export function SelectablePlanRadioOptions({
  hasSubscriptionOverride,
  purchasableSubscriptionOptions,
  currentSubscriptionRenewalMonths,
}: {
  hasSubscriptionOverride: boolean;
  purchasableSubscriptionOptions?: GQLSubscriptionOption[];
  currentSubscriptionRenewalMonths?: number;
}) {
  const { t } = useTranslation();
  const { currency, currencyFormatter } = useLocale();
  const applicableSubscriptionProducts: SubscriptionOption[] = useMemo(() => {
    if (hasSubscriptionOverride) {
      return []; // No plan change options for an override subscription.
    }

    const result: SubscriptionOption[] = [];
    if (purchasableSubscriptionOptions) {
      for (const option of purchasableSubscriptionOptions) {
        // filter out 3 month plan if the user is not subscribed to it
        if (option.renewalMonths === 3 && currentSubscriptionRenewalMonths !== 3) {
          continue;
        }
        result.push({
          ...option,
          billingCadence: gqlBillingCadenceToBillingCadence(option.billingCadence),
        });
      }
    }

    // return subscription options sorted so that the longest term is first
    return result.sort((a, b) => b.renewalMonths - a.renewalMonths);
  }, [currentSubscriptionRenewalMonths, hasSubscriptionOverride, purchasableSubscriptionOptions]);

  const radioOptions = applicableSubscriptionProducts.map((p) => (
    <Radio
      label={
        <Text fz={24} fw={600}>
          {getShortSubscriptionName(t, p.renewalMonths)}
        </Text>
      }
      value={p.sku}
      labelPosition="left"
      key={p.sku}
      color="dark"
      description={
        <Text fz={16} fw={600} color="type-light-secondary">
          {getBillingCadenceDescription(
            getLocalizedPricing(p.pricingInCents, currency),
            p.renewalMonths,
            currencyFormatter,
          )}
        </Text>
      }
      styles={(theme) => ({
        root: {
          padding: '20px 24px',
          borderRadius: 24,
          border: `2px solid ${theme.colors['background-light-tertiary'][0]}`,
        },
        body: {
          gap: 8,
        },
        labelWrapper: {
          flexGrow: 1,
        },
        inner: {
          alignSelf: 'center',
        },
      })}
    />
  ));
  return <Stack>{radioOptions}</Stack>;
}
